/* eslint-disable react/no-array-index-key */
/**
 * Appetite Settings › Risk Appetite Levels
 * Configurable matrix: columns = appetite levels (title + color), rows = criteria.
 * The level titles populate the Risk Appetite dropdown in taxonomy subcategory config.
 */
import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Box,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  IconButton,
  Tooltip,
  Typography,
  Alert,
  CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";

import DashboardLayout from "pages/LayoutContainers/DashboardLayout";
import DashboardNavbar from "pages/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import useFetchWithMsal from "hooks/useFetchWithMsal";
import BulkConfigIO from "components/BulkConfigIO";
import { protectedResources, siteData } from "authConfig";

const API = `${protectedResources.apiTodoList.endpoint}/appetite-levels/config`;
const BULK = `${protectedResources.apiTodoList.endpoint}/bulk-config/appetite-levels`;

// Purple shades — light to dark, matching the app brand colour
const PALETTE = ["#9c27b0", "#7b1fa2", "#6a1b9a", "#4a148c", "#38006b"];

function newId() {
  return Math.random().toString(36).slice(2, 10);
}

function buildDefaultCells(levels) {
  return levels.map((l) => ({ levelId: l.id, text: "" }));
}

export default function RiskAppetiteLevels() {
  const { execute } = useFetchWithMsal({ scopes: [siteData?.apiScopes] });
  const executeRef = useRef(execute);
  useEffect(() => {
    executeRef.current = execute;
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const [complianceStatement, setComplianceStatement] = useState("");
  const [levels, setLevels] = useState([]);
  const [criteria, setCriteria] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await executeRef.current("GET", API);
      const data = res?.data ?? res;
      setComplianceStatement(data?.complianceStatement || "");
      setLevels(data?.levels || []);
      setCriteria(data?.criteria || []);
    } catch {
      setError("Could not load appetite levels config.");
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // ── Levels (columns) ──────────────────────────────────────────────
  const addLevel = () => {
    if (levels.length >= 5) return;
    const id = newId();
    const color = PALETTE[levels.length] || "#888888";
    const newLevel = { id, order: levels.length + 1, title: `Level ${levels.length + 1}`, color };
    setLevels((prev) => [...prev, newLevel]);
    setCriteria((prev) =>
      prev.map((c) => ({ ...c, cells: [...c.cells, { levelId: id, text: "" }] }))
    );
  };

  const removeLevel = (levelId) => {
    setLevels((prev) => prev.filter((l) => l.id !== levelId));
    setCriteria((prev) =>
      prev.map((c) => ({ ...c, cells: c.cells.filter((cell) => cell.levelId !== levelId) }))
    );
  };

  const updateLevelTitle = (levelId, title) => {
    setLevels((prev) => prev.map((l) => (l.id === levelId ? { ...l, title } : l)));
  };

  // ID139 — each level's colour is admin-configurable and shown on the home page + taxonomy.
  const updateLevelColor = (levelId, color) => {
    setLevels((prev) => prev.map((l) => (l.id === levelId ? { ...l, color } : l)));
  };

  // ── Criteria (rows) ────────────────────────────────────────────────
  const addCriterion = () => {
    setCriteria((prev) => [
      ...prev,
      {
        id: newId(),
        order: prev.length + 1,
        label: "New criterion",
        mandatory: false,
        cells: buildDefaultCells(levels),
      },
    ]);
  };

  const removeCriterion = (criterionId) => {
    setCriteria((prev) => prev.filter((c) => c.id !== criterionId));
  };

  const updateCriterionLabel = (criterionId, label) => {
    setCriteria((prev) => prev.map((c) => (c.id === criterionId ? { ...c, label } : c)));
  };

  const updateCell = (criterionId, levelId, text) => {
    setCriteria((prev) =>
      prev.map((c) => {
        if (c.id !== criterionId) return c;
        const hasCell = c.cells.some((cell) => cell.levelId === levelId);
        const cells = hasCell
          ? c.cells.map((cell) => (cell.levelId === levelId ? { ...cell, text } : cell))
          : [...c.cells, { levelId, text }];
        return { ...c, cells };
      })
    );
  };

  const getCellText = (criterion, levelId) =>
    criterion.cells.find((cell) => cell.levelId === levelId)?.text || "";

  // ── Save ───────────────────────────────────────────────────────────
  const handleSave = async () => {
    setError(null);
    setSuccess(false);

    // ID139 — every level must have a colour set.
    const noColour = levels.find((l) => !String(l.color || "").trim());
    if (noColour) {
      setError(`Set a colour for "${noColour.title || "each level"}" before saving.`);
      return;
    }

    // Validate: every mandatory row must have a value for every level
    const invalidRow = criteria
      .filter((c) => c.mandatory)
      .find((row) =>
        levels.some((level) => {
          const match = row.cells.find((cl) => cl.levelId === level.id);
          return !match?.text?.trim();
        })
      );
    if (invalidRow) {
      setError(
        `"${invalidRow.label}" is required — please enter a description for every appetite level before saving.`
      );
      return;
    }

    setSaving(true);
    try {
      await executeRef.current("PUT", API, { complianceStatement, levels, criteria });
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch {
      setError("Could not save. Please try again.");
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox display="flex" justifyContent="center" mt={6}>
          <CircularProgress />
        </MDBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox px={3} pt={2} pb={4}>
        <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <MDTypography variant="h5" fontWeight="medium">
            Risk Appetite Levels
          </MDTypography>
          <MDBox display="flex" gap={1} alignItems="center">
            {/* Bulk edit: download the current appetite matrix as .xlsx, edit, re-import with a preview. */}
            <BulkConfigIO
              title="Risk Appetite Levels"
              exportUrl={`${BULK}/export`}
              previewUrl={`${BULK}/preview`}
              applyUrl={`${BULK}/apply`}
              filename="risk-appetite-levels.xlsx"
              onApplied={load}
              helpText="The file is the full config: keep an ID to modify a row, blank it to add, remove a row to delete it. Mandatory criteria need text in every level."
            />
            {/* Discard — revert any unsaved edits back to the last saved config. */}
            <MDButton
              variant="outlined"
              onClick={load}
              disabled={saving}
              sx={{
                color: "#52009b",
                borderColor: "#52009b",
                textTransform: "none",
                "&:hover": { borderColor: "#3a0070", backgroundColor: "rgba(82,0,155,0.06)" },
              }}
            >
              Discard
            </MDButton>
            <MDButton
              variant="contained"
              onClick={handleSave}
              disabled={saving}
              sx={{
                color: "#f7f4fb",
                backgroundColor: "#52009b",
                textTransform: "none",
                "&:hover": { backgroundColor: "#3a0070" },
              }}
            >
              {saving ? "Saving…" : "Save"}
            </MDButton>
          </MDBox>
        </MDBox>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Saved successfully.
          </Alert>
        )}

        {/* Matrix */}
        <Box sx={{ overflowX: "auto" }}>
          <Table
            sx={{
              tableLayout: "fixed",
              width: "100%",
              borderCollapse: "collapse",
              "& td, & th": {
                border: "1px solid #bdbdbd",
                verticalAlign: "top",
                padding: "10px 12px",
              },
            }}
          >
            {/* colgroup for consistent widths */}
            <colgroup>
              <col style={{ width: 180 }} />
              {levels.map((l) => (
                <col key={l.id} style={{ width: `${Math.floor(680 / levels.length)}px` }} />
              ))}
              <col style={{ width: 48 }} />
            </colgroup>

            <TableHead>
              <TableRow>
                {/* Criteria label column header */}
                <TableCell
                  sx={{
                    bgcolor: "#37474f",
                    color: "#52009b",
                    fontWeight: 700,
                    fontSize: 14,
                    letterSpacing: 0.3,
                  }}
                >
                  Criteria
                </TableCell>

                {/* One column per level */}
                {levels.map((level, idx) => (
                  <TableCell
                    key={level.id}
                    sx={{ bgcolor: level.color, color: "#52009b", py: 1.5, px: 1.5 }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: "rgba(82,0,155,0.75)",
                        fontWeight: 600,
                        display: "block",
                        mb: 0.5,
                        fontSize: 11,
                      }}
                    >
                      {idx + 1}
                    </Typography>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <TextField
                        value={level.title}
                        onChange={(e) => updateLevelTitle(level.id, e.target.value)}
                        variant="standard"
                        fullWidth
                        inputProps={{ style: { color: "#52009b", fontWeight: 700, fontSize: 14 } }}
                        sx={{
                          "& .MuiInput-underline:before": {
                            borderBottomColor: "rgba(82,0,155,0.35)",
                          },
                          "& .MuiInput-underline:hover:before": { borderBottomColor: "#52009b" },
                          "& .MuiInput-underline:after": { borderBottomColor: "#52009b" },
                        }}
                      />
                      {levels.length > 2 && (
                        <Tooltip title="Remove level">
                          <IconButton
                            size="small"
                            onClick={() => removeLevel(level.id)}
                            sx={{ color: "rgba(82,0,155,0.7)", p: 0.25, flexShrink: 0 }}
                          >
                            <DeleteIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                    {/* ID139 — required colour picker per level */}
                    <Box display="flex" alignItems="center" gap={0.5} mt={0.75}>
                      <Tooltip title="Set the colour for this appetite level">
                        <Box
                          component="input"
                          type="color"
                          value={level.color || "#888888"}
                          onChange={(e) => updateLevelColor(level.id, e.target.value)}
                          sx={{
                            width: 28,
                            height: 22,
                            p: 0,
                            border: "1px solid rgba(82,0,155,0.4)",
                            borderRadius: "4px",
                            cursor: "pointer",
                            bgcolor: "transparent",
                          }}
                        />
                      </Tooltip>
                      <Typography
                        variant="caption"
                        sx={{ color: "rgba(82,0,155,0.75)", fontSize: 11 }}
                      >
                        {level.color || "—"}
                      </Typography>
                    </Box>
                  </TableCell>
                ))}

                {/* Add level button column */}
                <TableCell sx={{ bgcolor: "#eceff1", textAlign: "center", p: "4px !important" }}>
                  {levels.length < 5 && (
                    <Tooltip title="Add level">
                      <IconButton size="small" onClick={addLevel} sx={{ color: "#52009b" }}>
                        <AddIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {criteria.map((criterion) => (
                <TableRow key={criterion.id} sx={{ "&:nth-of-type(even)": { bgcolor: "#fafafa" } }}>
                  {/* Criterion label */}
                  <TableCell sx={{ bgcolor: "#f5f5f5", verticalAlign: "middle" }}>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <TextField
                        value={criterion.label}
                        onChange={(e) => updateCriterionLabel(criterion.id, e.target.value)}
                        variant="standard"
                        disabled={criterion.mandatory}
                        fullWidth
                        inputProps={{ style: { fontWeight: 700, fontSize: 13, color: "#37474f" } }}
                        sx={{
                          "& .MuiInput-underline:before": { borderBottomColor: "transparent" },
                          "& .MuiInput-underline.Mui-disabled:before": {
                            borderBottomStyle: "none",
                          },
                        }}
                      />
                      {!criterion.mandatory && (
                        <Tooltip title="Remove row">
                          <IconButton
                            size="small"
                            onClick={() => removeCriterion(criterion.id)}
                            sx={{ color: "#b71c1c", p: 0.25, flexShrink: 0 }}
                          >
                            <DeleteIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>

                  {/* One cell per level */}
                  {levels.map((level) => (
                    <TableCell key={level.id} sx={{ p: "8px 12px !important" }}>
                      <TextField
                        value={getCellText(criterion, level.id)}
                        onChange={(e) => updateCell(criterion.id, level.id, e.target.value)}
                        multiline
                        fullWidth
                        variant="standard"
                        minRows={2}
                        placeholder="Enter description…"
                        inputProps={{ style: { fontSize: 12 } }}
                        sx={{
                          "& .MuiInput-underline:before": {
                            borderBottomColor: "transparent",
                          },
                          "& .MuiInput-underline:hover:before": { borderBottomColor: "#ccc" },
                        }}
                      />
                    </TableCell>
                  ))}

                  {/* Empty cell under add-level column */}
                  <TableCell sx={{ bgcolor: "#f5f5f5" }} />
                </TableRow>
              ))}

              {/* Add criterion row */}
              <TableRow>
                <TableCell colSpan={levels.length + 2} sx={{ bgcolor: "#fafafa", py: 0.5 }}>
                  <MDButton
                    variant="text"
                    color="info"
                    startIcon={<AddIcon />}
                    onClick={addCriterion}
                    size="small"
                  >
                    Add criteria row
                  </MDButton>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Box>

        <Typography variant="caption" color="text.secondary" display="block" mt={1.5}>
          Level titles are used as the Risk Appetite options in taxonomy subcategory configuration.
          Minimum 2 levels, maximum 5 levels.
        </Typography>
      </MDBox>
    </DashboardLayout>
  );
}
